import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const Container = styled.div`
  padding: 20px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;

  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
  }

  select {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 12px;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px 10px;
  }

  a {
    color: ${({ colors }) => colors.buttonBackground};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const InfoButton = styled.button`
  margin-left: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid ${({ colors }) => colors.border};
  background: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.buttonBackground};
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.buttonBackground};
    color: #fff;
  }
`;

const DetailBlock = styled.div`
  text-align: left;
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};
  line-height: 1.45;
`;

const DetailRow = styled.div`
  margin-bottom: 8px;
  word-break: break-word;

  strong {
    display: inline-block;
    min-width: 140px;
  }
`;

const INFO_TEMPLATES = new Set(["forget_password_tool", "send_otp"]);

const parseParams = (params) => {
  if (!params) return {};
  if (typeof params === "object") return params;
  if (typeof params === "string") {
    try {
      return JSON.parse(params);
    } catch (err) {
      return {};
    }
  }
  return {};
};

const getTemplateInfoRows = (log) => {
  if (!INFO_TEMPLATES.has(log?.template)) {
    return null;
  }

  const parsed = parseParams(log?.params);

  if (log.template === "forget_password_tool") {
    const name = parsed?.name ?? "";
    const email = parsed?.email || log?.email || "";
    const url = parsed?.url || "";
    return [
      { label: "Name", value: name || "N/A" },
      { label: "Email", value: email || "N/A" },
      { label: "Password Reset URL", value: url || "N/A" },
    ];
  }

  if (log.template === "send_otp") {
    const name = parsed?.name || parsed?.user?.name || "N/A";
    const otp = parsed?.otp ?? "N/A";
    return [
      { label: "Name", value: String(name) },
      { label: "OTP", value: String(otp) },
    ];
  }

  return null;
};

export default function EmailLogContentComponent({ colors }) {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(0); // zero-based
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // fetch page of email logs
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const resp = await API.getEmailLog({
          page,
          items_per_page: itemsPerPage,
        });
        setLogs(resp.result || []);
        setTotal(Number(resp.total) || 0);
      } catch (err) {
        console.error("Failed to load email logs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [page, itemsPerPage]);

  // calculate displayed range
  const start = logs.length ? page * itemsPerPage + 1 : 0;
  const end =
    itemsPerPage === -1 ? total : Math.min((page + 1) * itemsPerPage, total);

  const openInfo = (rows) => {
    setSelectedRows(rows || []);
    setInfoOpen(true);
  };

  return (
    <Container>
      <ConfirmationModal
        show={infoOpen}
        title="Email Details"
        message={null}
        onCancel={() => setInfoOpen(false)}
        onConfirm={() => setInfoOpen(false)}
        single
        confirmLabel="Close"
      >
        <DetailBlock colors={colors}>
          {selectedRows.map((row) => (
            <DetailRow key={row.label}>
              <strong>{row.label}:</strong> {row.value}
            </DetailRow>
          ))}
        </DetailBlock>
      </ConfirmationModal>

      <Controls colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(0);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label style={{ marginLeft: 8 }}>entries</label>
        </div>
      </Controls>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Sender</th>
                  <th>Template</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const infoRows = getTemplateInfoRows(log);
                  return (
                    <tr key={i}>
                      <td>{log.scheduled_time}</td>
                      <td>
                        {log.email}
                        {infoRows ? (
                          <InfoButton
                            colors={colors}
                            type="button"
                            aria-label={`View details for ${log.template}`}
                            onClick={() => openInfo(infoRows)}
                          >
                            i
                          </InfoButton>
                        ) : null}
                      </td>
                      <td>{log.subject}</td>
                      <td>{log.email_from}</td>
                      <td>
                        <code>{log.template}</code>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>

          <InfoText colors={colors}>
            Showing {start} to {end} of {total} entries
          </InfoText>

          <Pagination colors={colors}>
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={end === total}
            >
              Next
            </button>
          </Pagination>
        </>
      )}
    </Container>
  );
}
